<template>
  <div class="tetris-widget code-line">
    <div class="tetris-caption">
      <span class="code-comment">// competences.stack()</span>
    </div>
    <div
      class="tetris-board"
      role="img"
      :aria-label="ariaLabel"
    >
      <div class="tetris-layer tetris-cells" :style="boardStyle">
        <div
          v-for="cell in backgroundCells"
          :key="cell"
          class="tetris-cell"
        />
      </div>
      <div class="tetris-layer tetris-blocks" :style="boardStyle">
        <div
          v-for="block in visibleBlocks"
          :key="block.key"
          class="tetris-block"
          :style="blockStyle(block)"
        >
          <span class="tetris-block-label">{{ block.label }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  COLS,
  ROWS,
  blocksFromBoard,
  canSpawnAny,
  collides,
  competenceColor,
  createEmptyBoard,
  findBestPlacement,
  lockPieceOnBoard,
  pickRandomCompetence,
  buildStaticBoard,
} from '../utils/competenceTetris';

const TICK_MS = 280;

export default {
  name: 'CompetenceTetris',
  props: {
    competences: {
      type: Array,
      required: true,
    },
  },
  data() {
    return {
      board: createEmptyBoard(),
      activePiece: null,
      timerId: null,
      reducedMotion: false,
      colorIndex: 0,
    };
  },
  computed: {
    boardStyle() {
      return {
        gridTemplateColumns: `repeat(${COLS}, 1fr)`,
        gridTemplateRows: `repeat(${ROWS}, 1fr)`,
      };
    },
    backgroundCells() {
      return Array.from({ length: COLS * ROWS }, (_, index) => `cell-${index}`);
    },
    visibleBlocks() {
      const placed = blocksFromBoard(this.board).map((block) => ({
        ...block,
        key: `placed-${block.id}`,
      }));

      if (!this.activePiece) {
        return placed;
      }

      return [
        ...placed,
        {
          ...this.clipBlock(this.activePiece),
          key: 'active',
        },
      ];
    },
    ariaLabel() {
      return this.reducedMotion
        ? 'Static view of professional competences'
        : 'Auto-playing competence stacking animation';
    },
  },
  mounted() {
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (this.reducedMotion) {
      this.board = buildStaticBoard(this.competences);
      return;
    }

    this.spawnPiece();
    this.timerId = window.setInterval(this.tick, TICK_MS);
  },
  beforeUnmount() {
    if (this.timerId !== null) {
      window.clearInterval(this.timerId);
    }
  },
  methods: {
    clipBlock(block) {
      if (block.y >= 0) {
        return block;
      }

      const clipRows = -block.y;
      return {
        ...block,
        y: 0,
        height: block.height - clipRows,
      };
    },
    blockStyle(block) {
      return {
        gridColumn: `${block.x + 1} / span ${block.width}`,
        gridRow: `${block.y + 1} / span ${block.height}`,
        backgroundColor: block.color,
      };
    },
    spawnPiece() {
      if (!canSpawnAny(this.board, this.competences)) {
        this.board = createEmptyBoard();
      }

      const competence = pickRandomCompetence(this.competences);
      const placement = findBestPlacement(this.board, competence);

      if (!placement) {
        this.board = createEmptyBoard();
        this.spawnPiece();
        return;
      }

      const color = competenceColor(competence, this.colorIndex);
      this.colorIndex += 1;

      this.activePiece = {
        id: competence.id,
        label: competence.label,
        color,
        x: placement.x,
        y: -placement.height,
        width: placement.width,
        height: placement.height,
      };
    },
    tick() {
      if (!this.activePiece) {
        this.spawnPiece();
        return;
      }

      const { width, height, x, y } = this.activePiece;
      const nextY = y + 1;

      if (!collides(this.board, width, height, x, nextY)) {
        this.activePiece = { ...this.activePiece, y: nextY };
        return;
      }

      this.board = lockPieceOnBoard(this.board, this.activePiece);
      this.activePiece = null;
    },
  },
};
</script>

<style scoped>
.tetris-widget {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.tetris-caption {
  font-size: 0.85rem;
}

.code-comment {
  color: #6a9955;
}

.tetris-board {
  position: relative;
  width: 100%;
  max-width: 520px;
  aspect-ratio: 10 / 14;
  margin: 0 auto;
  padding: 4px;
  background-color: #1a1a1a;
  border-radius: 4px;
  border: 1px solid #333;
}

.tetris-layer {
  position: absolute;
  inset: 4px;
  display: grid;
  gap: 2px;
}

.tetris-cell {
  background-color: #252526;
  border-radius: 2px;
  min-height: 0;
}

.tetris-block {
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 3px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  box-shadow: inset 0 0 0 1px rgba(0, 0, 0, 0.2);
  overflow: hidden;
  z-index: 1;
  min-width: 0;
  min-height: 0;
}

.tetris-block-label {
  color: #1e1e1e;
  font-size: clamp(0.45rem, 1.6vw, 0.7rem);
  font-weight: 600;
  text-align: center;
  padding: 0 2px;
  line-height: 1.1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 100%;
}
</style>
